import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import BottomNav from "@/components/BottomNav";
import {
  ArrowLeft, DollarSign, MessageSquare, Image as ImageIcon, Users, Sparkles, TrendingUp, RefreshCw,
} from "lucide-react";

/**
 * Admin cost + usage dashboard.
 * Uses cost estimates from /api/admin/usage (see backend/admin_usage.py for
 * assumptions). Values are ESTIMATES — Emergent pass-through pricing may vary.
 */
export default function AdminUsage() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/usage");
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load usage.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading && !data) return <div className="mx-auto max-w-2xl p-6 text-neutral-400">Loading usage…</div>;
  if (error) return (
    <div className="mx-auto max-w-2xl p-6 text-rose-300" data-testid="admin-usage-error">
      {error}
    </div>
  );
  if (!data) return null;

  const projection = data.monthly_projection_usd;
  const projectionBand = projection < 20 ? "green" : projection < 100 ? "amber" : "rose";

  return (
    <div className="mx-auto max-w-3xl px-4 pb-32 pt-safe-6">
      <header className="mb-6 flex items-center gap-3">
        <button
          data-testid="admin-usage-back-btn"
          onClick={() => nav(-1)}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-white/70 hover:bg-white/10"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <div className="font-display text-xs uppercase tracking-[0.35em] text-amber-400/80">Admin</div>
          <h1 className="font-display text-3xl leading-tight text-white">Cost & Usage</h1>
        </div>
        <button
          data-testid="admin-usage-refresh-btn"
          onClick={load}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-white/70 hover:bg-white/10"
          aria-label="Refresh"
        >
          <RefreshCw size={16} />
        </button>
      </header>

      {/* -------- Headline: monthly projection -------- */}
      <section
        data-testid="admin-usage-projection"
        className={`mb-6 rounded-3xl border p-6 ${
          projectionBand === "green" ? "border-emerald-500/30 bg-emerald-500/5"
            : projectionBand === "amber" ? "border-amber-500/30 bg-amber-500/5"
            : "border-rose-500/30 bg-rose-500/5"
        }`}
      >
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-400">
          <TrendingUp size={12} /> Projected monthly cost (7d × 30/7)
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="font-display text-5xl text-white" data-testid="admin-usage-projection-value">
            ${projection.toFixed(2)}
          </span>
          <span className="text-sm text-neutral-500">/ month</span>
        </div>
        <p className="mt-3 text-xs text-neutral-400 leading-relaxed">
          Based on the last 7 days of assistant messages + avatar generations, using published
          Anthropic and Nano Banana rates. Emergent pass-through pricing may vary. Real usage
          will fluctuate with active users.
        </p>
      </section>

      {/* -------- 24h / 7d / 30d windows -------- */}
      <div className="mb-6 grid grid-cols-3 gap-3">
        <WindowCard label="Last 24 hours" w={data.windows.day} testId="admin-usage-window-day" />
        <WindowCard label="Last 7 days" w={data.windows.week} testId="admin-usage-window-week" highlight />
        <WindowCard label="Last 30 days" w={data.windows.month} testId="admin-usage-window-month" />
      </div>

      {/* -------- Where the money goes (breakdown of the 7d window) -------- */}
      <section className="mb-6 rounded-2xl border border-white/5 bg-white/[0.02] p-4">
        <h2 className="mb-3 text-xs uppercase tracking-widest text-neutral-500">Where the money went (last 7 days)</h2>
        <BreakdownRow icon={MessageSquare} label={`Assistant messages (${data.windows.week.assistant_messages})`} value={data.windows.week.cost_breakdown.messages_usd} />
        <BreakdownRow icon={ImageIcon} label={`Avatar generations (${data.windows.week.image_generations})`} value={data.windows.week.cost_breakdown.images_usd} />
        <div className="mt-2 border-t border-white/5 pt-2">
          <BreakdownRow label="Total" value={data.windows.week.estimated_cost_usd} bold />
        </div>

        {/* Model split for the 7d window */}
        {Object.keys(data.windows.week.assistant_messages_by_model || {}).length > 0 && (
          <div className="mt-4">
            <h3 className="mb-2 text-[10px] uppercase tracking-widest text-neutral-500">By model</h3>
            <div className="space-y-1">
              {Object.entries(data.windows.week.assistant_messages_by_model).map(([m, n]) => (
                <div key={m} className="flex items-center justify-between text-xs">
                  <span className="text-neutral-300">
                    {m === "claude-opus-4-8" ? (
                      <>Opus 4.8 <span className="ml-1 rounded-full bg-amber-500/20 border border-amber-400/40 px-1.5 py-[1px] text-[8px] text-amber-200">Premium</span></>
                    ) : "Sonnet 4.6"}
                  </span>
                  <span className="text-neutral-400 tabular-nums">
                    {n} × ${(data.cost_model.cost_per_message_usd[m] || 0.018).toFixed(3)} = ${((data.cost_model.cost_per_message_usd[m] || 0.018) * n).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* -------- All-time totals -------- */}
      <section className="mb-6 rounded-2xl border border-white/5 bg-white/[0.02] p-4">
        <h2 className="mb-3 text-xs uppercase tracking-widest text-neutral-500">All-time totals</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat icon={Users}         label="Users"        value={data.totals.users} testId="admin-usage-total-users" />
          <Stat icon={Sparkles}      label="Characters"   value={data.totals.characters} testId="admin-usage-total-chars" />
          <Stat icon={MessageSquare} label="Chats"        value={data.totals.chats} testId="admin-usage-total-chats" />
          <Stat icon={DollarSign}    label="Msgs (all)"   value={data.totals.assistant_messages_all_time} testId="admin-usage-total-msgs" />
        </div>
      </section>

      {/* -------- Cost model reference -------- */}
      <section className="mb-6 rounded-2xl border border-white/5 bg-white/[0.02] p-4">
        <h2 className="mb-3 text-xs uppercase tracking-widest text-neutral-500">Cost model (per unit)</h2>
        <ul className="space-y-1.5 text-xs text-neutral-300">
          <li className="flex justify-between"><span>Claude Sonnet 4.6 reply</span><span className="tabular-nums">${data.cost_model.cost_per_message_usd["claude-sonnet-4-6"]?.toFixed(3)}</span></li>
          <li className="flex justify-between"><span>Claude Opus 4.8 reply</span><span className="tabular-nums">${data.cost_model.cost_per_message_usd["claude-opus-4-8"]?.toFixed(3)}</span></li>
          <li className="flex justify-between"><span>Nano Banana avatar</span><span className="tabular-nums">${data.cost_model.cost_per_image_usd?.toFixed(3)}</span></li>
        </ul>
        <p className="mt-3 text-[11px] text-neutral-500 leading-relaxed">
          {data.cost_model.assumptions}
        </p>
      </section>

      {/* -------- Practical guidance -------- */}
      <section className="mb-6 rounded-2xl border border-violet-500/20 bg-violet-500/5 p-4">
        <h2 className="mb-2 text-xs uppercase tracking-widest text-violet-300">To keep the lights on</h2>
        <ul className="space-y-2 text-xs text-neutral-300 leading-relaxed">
          <li>• Turn on <strong>Auto Top-Up</strong> in Emergent → Profile → Manage plan → Universal Key. This ensures the app never stops when a burst of users comes in overnight.</li>
          <li>• Keep Sonnet 4.6 as the default for everyone. Gate Opus 4.8 behind <strong>Immersive+</strong> ($14.99/mo) so paid revenue directly offsets the premium model.</li>
          <li>• Watch this dashboard weekly. If the 7-day projection creeps toward your comfort limit, tighten free-tier limits or raise Immersive+ price.</li>
        </ul>
      </section>

      <BottomNav />
    </div>
  );
}

function WindowCard({ label, w, testId, highlight }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border p-3 ${highlight ? "border-violet-500/30 bg-violet-500/[0.06]" : "border-white/5 bg-white/[0.02]"}`}
    >
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="mt-1.5 font-display text-2xl leading-none text-white tabular-nums">
        ${w.estimated_cost_usd.toFixed(2)}
      </div>
      <div className="mt-2 space-y-0.5 text-[11px] text-neutral-400">
        <div className="flex items-center justify-between"><span>Messages</span><span className="tabular-nums">{w.assistant_messages}</span></div>
        <div className="flex items-center justify-between"><span>Images</span><span className="tabular-nums">{w.image_generations}</span></div>
        <div className="flex items-center justify-between"><span>Active users</span><span className="tabular-nums">{w.active_users}</span></div>
      </div>
    </div>
  );
}

function BreakdownRow({ icon: Icon, label, value, bold }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className={`flex items-center gap-2 text-sm ${bold ? "font-medium text-white" : "text-neutral-300"}`}>
        {Icon && <Icon size={14} className="text-neutral-500" />}
        {label}
      </span>
      <span className={`tabular-nums ${bold ? "text-white text-lg font-display" : "text-neutral-300 text-sm"}`}>
        ${value.toFixed(2)}
      </span>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-xl bg-white/[0.03] p-3" data-testid={testId}>
      <Icon size={14} className="mb-1 text-neutral-500" />
      <div className="font-display text-xl text-white tabular-nums">{value.toLocaleString()}</div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
    </div>
  );
}
